/**
 * Guest-facing quote copy — ONE place for every sentence a seating quote can
 * say (ALL_DAY_RIBBON_PLAN.md D12, owner 2026-09-02).
 *
 * Why a module: the single-ask quote (quoteService) and the all-day profile
 * (profileCore / RibbonPicker) must say the same thing for the same fact, and
 * the strings travel into SMS, so every one of them must survive GSM-7
 * unchanged (no em dashes, no curly quotes, no emoji).
 *
 * There is no "fully booked". A busy floor is a longer wait, and the engine
 * already prices it as a later promise; the only real refusal is "no table
 * before the 2:00 PM last seating", and it says exactly that.
 *
 * Pure: callers pass already-formatted times ("11:00 AM").
 */

export const LAST_SEAT_DISPLAY = '2:00 PM';

/**
 * The on-site cap a party on the way is held to (quoteService ON_SITE_CAP_MIN):
 * the arrive-by is set so they wait no longer than this once they are here.
 */
export const WAIT_BAND_MIN = 15;

/**
 * The guest's wait, said the same way everywhere it is said: the public form's
 * readout and long-wait question, its done page, every welcome and reminder
 * text, and the place page (owner 2026-10-02/03).
 *
 *   inside the cap   "up to a 13 min wait"
 *   past the cap     "20 to 35 min wait": a 15-minute band ending at the wait
 *                    ("n to n+15"). Only a guest who kept an asked time over
 *                    the later arrive-by waits past the cap, and the band is
 *                    computed from the window alone, so every surface agrees
 *                    without any of them storing a low end.
 */
export function waitPhrase(highMin: number): string {
  const hi = Math.max(1, Math.round(highMin));
  return hi > WAIT_BAND_MIN
    ? `${hi - WAIT_BAND_MIN} to ${hi} min wait`
    : `up to ${articleFor(hi)} ${hi} min wait`;
}

/** "an 8", "an 11", "an 18", "an 80"; "a" for every other minute count a wait can be */
function articleFor(n: number): string {
  return /^(8|11|18|8\d)$/.test(String(n)) ? 'an' : 'a';
}

/** "Up to a 13 min wait" / "20 to 35 min wait": a waitPhrase that starts a line */
export function waitLine(highMin: number): string {
  const p = waitPhrase(highMin);
  return p.charAt(0).toUpperCase() + p.slice(1);
}

/**
 * The same wait as the public form's wheel says it in its band (owner
 * 2026-10-10, mockup frame D2): a lead-in on the line above the bar, the
 * minutes big after the bar, "wait" small after them. Inside the cap
 * "up to a" / "13 min" / "wait"; past it "expect a" / "20 to 35 min" / "wait".
 * The numbers are waitPhrase's, so the wheel, the done page and every text
 * agree; only the lead-in past the cap is the screen's own.
 */
export function waitPieces(highMin: number): { lead: string; big: string; tail: string } {
  const hi = Math.max(1, Math.round(highMin));
  return hi > WAIT_BAND_MIN
    ? { lead: 'expect a', big: `${hi - WAIT_BAND_MIN} to ${hi} min`, tail: 'wait' }
    : { lead: `up to ${articleFor(hi)}`, big: `${hi} min`, tail: 'wait' };
}

/**
 * The arrive-by sentence every guest TEXT carries (owner 2026-10-22: "the
 * language for the wait should always be 'Please arrive by XX:XX and expect up
 * to a XX wait.'"): "Please arrive by 10:00 AM and expect up to a 13 min
 * wait." Past the on-site cap it keeps the band (owner 2026-10-02, never "up
 * to" there): "Please arrive by 11:00 AM and expect a 20 to 35 min wait." A
 * wait of 0 is the planner's zero wait: zeroWaitSentence. Screens keep their
 * own shorter waitLine.
 */
export function arriveByWaitSentence(start: string, highMin: number): string {
  const hi = Math.round(highMin);
  if (hi <= 0) return zeroWaitSentence(start);
  const wait = hi > WAIT_BAND_MIN ? `a ${hi - WAIT_BAND_MIN} to ${hi} min wait` : waitPhrase(hi);
  return `Please arrive by ${start} and expect ${wait}.`;
}

/** a zero-wait booking's sentence (owner 2026-10-22): the table is theirs from the arrive-by */
export function zeroWaitSentence(start: string): string {
  return `Please arrive by ${start} and your table will be ready.`;
}

/** both ends of a window are the same minute: a zero-wait promise */
export function isZeroWaitDisplay(start: string | null | undefined, end: string | null | undefined): boolean {
  const a = displayToMin(start);
  const b = displayToMin(end);
  return a !== null && b !== null && a === b;
}

/**
 * The public form's question when the wait at the chosen time is past the
 * on-site cap (owner 2026-10-02): the band at the asked time, and the later
 * arrival that keeps the wait inside the cap. `asked` is "11:00 AM", or null
 * for the Now row ("Arriving now ...").
 */
export function longWaitCopy(asked: string | null, highMin: number, suggest: string): string {
  const hi = Math.max(1, Math.round(highMin));
  const wait = hi > WAIT_BAND_MIN
    ? `a wait of between ${hi - WAIT_BAND_MIN} and ${hi} minutes`
    : `a wait of up to ${hi} minutes`;
  return `Arriving ${asked ? `at ${asked}` : 'now'} means ${wait}, so we suggest coming at ${suggest} instead.`;
}

/** a future day already full at the asked time, said once (the picker's own line; the server's longer dayFullCopy is unchanged) */
export function dayFullShortCopy(): string {
  return `No table before our ${LAST_SEAT_DISPLAY} last seating.`;
}

/** "10:00 AM" -> 600 (minutes since midnight); null when it is not a 12-hour time */
export function displayToMin(display: string | null | undefined): number | null {
  const m = (display || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return null;
  const h = Number(m[1]) % 12 + (m[3].toUpperCase() === 'PM' ? 12 : 0);
  return h * 60 + Number(m[2]);
}

/** minutes from one display time to a later one ("10:00 AM", "10:13 AM" -> 13); null when either is unreadable or the end is not later */
export function minutesBetweenDisplays(start: string | null | undefined, end: string | null | undefined): number | null {
  const a = displayToMin(start);
  const b = displayToMin(end);
  return a === null || b === null || b <= a ? null : b - a;
}

/** the normal window card: arrive-by and seated-before */
export function windowCopy(start: string, end: string): string {
  return `Arrive by ${start} and we will seat you before ${end}.`;
}

/**
 * The on-site cap slid the arrive-by later than the ask. With `waitMin` the
 * sentence names the wait the guest would face by coming at the asked time
 * (the ribbon knows it); without it, the single-ask phrasing stays as shipped.
 */
export function arriveLaterCopy(asked: string, start: string, capMin: number, waitMin?: number): string {
  const first = waitMin !== undefined
    ? `Coming at ${asked} would leave you waiting ${waitMin} minutes.`
    : `Coming at ${asked} would leave you waiting too long.`;
  return `${first} We recommend arriving at ${start} and expect a wait of up to ${capMin} minutes.`;
}

/** a party standing here (item 3): the honest range, said as when, never as a width or a cap */
export function presentWindowCopy(startLow: string, end: string): string {
  return `We will seat you by about ${end}, and it could be as early as ${startLow}.`;
}

/** same-day plan-overflow (item 3): the first table is past the last seating; the party is added, not refused */
export function overflowCopy(earliest: string): string {
  return `No table before ${LAST_SEAT_DISPLAY}. The first table we can name is about ${earliest}. You can still be added and we will seat you as soon as one frees up.`;
}

/** day-full for a future date: the honest refusal, with the latest time that still has a table when known */
export function dayFullCopy(asked: string, latestOpen: string | null): string {
  const base = `No table before ${LAST_SEAT_DISPLAY}. Arriving at ${asked}, there is no table before the ${LAST_SEAT_DISPLAY} last seating.`;
  return latestOpen ? `${base} The latest time with a table is ${latestOpen}.` : `${base} Please try an earlier time.`;
}

/** day-full on the same day: the door never refuses (owner rule), so the copy says what actually happens */
export function dayFullSameDayCopy(): string {
  return `No table before ${LAST_SEAT_DISPLAY}. You can still add yourself and we will seat you as a table frees up.`;
}

/** past the last seating on the same day: still never a refusal at the door */
export function lastSeatSameDayCopy(): string {
  return `Past the ${LAST_SEAT_DISPLAY} last seating. You can still add yourself and we will seat you if a table frees up.`;
}

/** no single table or combine fits, same day: the floor will have to make tables */
export function noTableFitsSameDayCopy(): string {
  return 'No single table or combine fits a party that size right now. You can still add yourself and we will put tables together as they free up.';
}

/** a verified earliest table exists later the same day (single-ask path only) */
export function earliestCopy(earliest: string): string {
  return `No table at that time. The first table we can stand behind is about ${earliest}.`;
}

export function lastSeatCopy(): string {
  return `Our last seating is ${LAST_SEAT_DISPLAY}. Please pick an earlier time.`;
}

export function callUsCopy(): string {
  return 'For a party that size, please call us so we can plan your tables.';
}

export function engineDownCopy(): string {
  return 'We could not compute a seating window. Please call us.';
}

/** the "no times left today" state of the ribbon (14:00 to the 14:30 close) */
export function noTimesLeftCopy(): string {
  return `Our last seating today was ${LAST_SEAT_DISPLAY}. Please pick another day.`;
}

/** every string above, for the GSM-7 test */
export function allQuoteCopy(): string[] {
  return [
    windowCopy('11:00 AM', '11:15 AM'),
    arriveLaterCopy('11:00 AM', '11:30 AM', 15),
    arriveLaterCopy('11:00 AM', '11:30 AM', 15, 45),
    presentWindowCopy('10:35 AM', '10:50 AM'),
    overflowCopy('2:20 PM'),
    dayFullCopy('1:45 PM', '1:35 PM'),
    dayFullCopy('1:45 PM', null),
    dayFullSameDayCopy(),
    lastSeatSameDayCopy(),
    noTableFitsSameDayCopy(),
    earliestCopy('12:40 PM'),
    lastSeatCopy(),
    callUsCopy(),
    engineDownCopy(),
    noTimesLeftCopy(),
    arriveByWaitSentence('10:00 AM', 13),
    arriveByWaitSentence('11:00 AM', 35),
    arriveByWaitSentence('10:00 AM', 0),
    longWaitCopy('11:00 AM', 35, '11:20 AM'),
    longWaitCopy(null, 12, '9:55 AM'),
    dayFullShortCopy(),
  ];
}
